import {useEffect} from 'react'
import Image from '@components/Image'
import NoSSR from '@components/NoSSR'
import SafeSuspenseLoading from '@components/SafeSuspenseLoading'
import Button from '@components/V3/Button'
import Modal from '@components/V3/Modal'
import {EVENTS} from '@helpers/analytics/events'
import {BrowseTaxonomies} from '@helpers/analytics/events/browse'
import {sendEvent} from '@helpers/analytics/sendEvents'
import useCookiesAnswered from '@hooks/useCookiesAnswered'
import useEventsBaseProperties from '@hooks/useEventsBaseProperties'
import useInitialData from '@page-components/Layout/useInitialData'
import {useTranslation} from 'next-i18next'

import usePreferenceAddressModal from './useAddressModalPreferences.main'

interface Props {
  isOpen?: boolean
  setOpen?: (isOpen: boolean) => void
  setOpenAddressModal?: (isOpen: boolean) => void
}

function AddressInstructionsModalInner(props: Props) {
  const {isOpen, setOpen, setOpenAddressModal} = props
  const {website} = useInitialData()
  const {t} = useTranslation('website', {keyPrefix: 'addressInstructions'})
  const cookiesAnswered = useCookiesAnswered()
  const baseEventProperties = useEventsBaseProperties()
  const {showAddressModal, submitAddressPreference} = usePreferenceAddressModal()

  useEffect(() => {
    if (isOpen && showAddressModal) {
      sendEvent<BrowseTaxonomies['addressStartViewed']>(
        EVENTS.browse.addressStartViewed,
        baseEventProperties
      )
    }
  }, [isOpen, showAddressModal])

  if (!isOpen || !showAddressModal) return null

  return (
    <Modal
      onClose={async () => {
        await setOpenAddressModal(false)
        await setOpen(false)
        submitAddressPreference(false)
      }}
      isOpen={isOpen}
      compact
      showClose={false}
      className="dark:bg-zinc-800 font-sans"
      canClickOutside={cookiesAnswered}
      header={
        <SafeSuspenseLoading>
          <div className="p-4">
            <div className="pb-1">
              <Image
                priority
                src={website.logo.url}
                alt={website.name || 'Logo'}
                title={website.name || 'logo'}
                objectFit="contain"
                height={75}
                width={75}
              />
            </div>
            <div className="font-normal">{t('title')}</div>
            <span className="font-bold text-3xl">{website.name}</span>
          </div>
        </SafeSuspenseLoading>
      }
      footer={
        <div className="pt-4 pb-8 flex justify-center">
          <div
            className="underline cursor-pointer"
            onClick={async () => {
              await setOpenAddressModal(false)
              await setOpen(false)
              submitAddressPreference(false)
            }}>
            {t('cancel')}
          </div>
        </div>
      }>
      <SafeSuspenseLoading>
        <div className="px-8 pb-4">
          {t('body')} <span className="font-bold">{t('bodyBold')}</span>
        </div>
        <div className="px-8 pb-4">
          <Button
            primary
            full
            big
            className="rounded-lg"
            onClick={async () => {
              await setOpen(false)
              setOpenAddressModal(true)
              submitAddressPreference(false)
            }}>
            {t('continueToDirections')}
          </Button>
        </div>
      </SafeSuspenseLoading>
    </Modal>
  )
}

export default function AddressInstructionsModal(props: Props) {
  return (
    <NoSSR>
      <AddressInstructionsModalInner {...props} />
    </NoSSR>
  )
}
